import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { SuburbSelect } from "@/components/SuburbSelect";
import { Loader2 } from "lucide-react";
import GlassCard from "@/components/GlassCard";
import Logo from "@/components/Logo";

const SetupAccount = () => {
    const { session, checkSetupComplete } = useAuth();
    const user = session?.user;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [popiaAccepted, setPopiaAccepted] = useState(false);
    const [formData, setFormData] = useState({
        fullName: "",
        propertyName: "",
        suburb: "",
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!popiaAccepted) return;
        setLoading(true);

        try {
            // 1. Create or Update Profile (Full Name + POPIA)
            // We use UPSERT in case the trigger failed to create the profile row
            const { error: profileError } = await supabase
                .from("profiles")
                .upsert({
                    id: user?.id,
                    email: user?.email,
                    full_name: formData.fullName,
                    is_onboarded: true,
                    popia_consent: true,
                    popia_consent_date: new Date().toISOString()
                });

            if (profileError) throw profileError;

            // 2. Insert First Property (The Alias)
            const { error: propertyError } = await supabase
                .from("properties")
                .insert([
                    {
                        name: formData.propertyName,
                        suburb: formData.suburb,
                        user_id: user?.id,
                        address: null,
                    },
                ]);

            if (propertyError) throw propertyError;

            toast.success("Welcome to HomeEzi!");

            // Force update the auth context state so ProtectedRoute knows we are done
            await checkSetupComplete(user.id);

            navigate("/");
        } catch (error) {
            toast.error(error.message || "An error occurred");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-[hsl(var(--gradient-start))] to-[hsl(var(--gradient-end))] p-6">
            <div className="w-full max-w-md space-y-8">
                {/* Logo and Title */}
                <div className="text-center space-y-4">
                    <div className="mb-4">
                        <Logo size="xl" />
                    </div>
                    <div className="space-y-1">
                        <h1 className="text-3xl font-extrabold tracking-tight text-foreground">Final Touches</h1>
                        <p className="text-muted-foreground font-medium">Let's personalize your experience.</p>
                    </div>
                </div>

                <GlassCard className="p-8 border-none bg-white/60 backdrop-blur-2xl shadow-2xl rounded-[2.5rem]">
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="space-y-2">
                            <Label htmlFor="fullName" className="text-xs font-bold uppercase tracking-wider text-muted-foreground ml-1">Your Full Name</Label>
                            <Input
                                id="fullName"
                                placeholder="e.g. John Smith"
                                required
                                value={formData.fullName}
                                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                                className="rounded-2xl h-12 bg-white/80 border-none shadow-sm focus-visible:ring-primary/20"
                            />
                        </div>

                        <div className="h-px bg-gray-100/50 my-2" />
                        <p className="text-[10px] font-bold text-primary/40 uppercase tracking-[0.2em] mb-1 ml-1">Home Location</p>

                        <div className="space-y-2">
                            <Label htmlFor="propertyName" className="text-xs font-bold uppercase tracking-wider text-muted-foreground ml-1">Home Nickname</Label>
                            <Input
                                id="propertyName"
                                placeholder="e.g. Primary Residence"
                                required
                                value={formData.propertyName}
                                onChange={(e) => setFormData({ ...formData, propertyName: e.target.value })}
                                className="rounded-2xl h-12 bg-white/80 border-none shadow-sm focus-visible:ring-primary/20"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="suburb" className="text-xs font-bold uppercase tracking-wider text-muted-foreground ml-1">Suburb / Area</Label>
                            <SuburbSelect
                                value={formData.suburb}
                                onSelect={(val) => setFormData({ ...formData, suburb: val })}
                                placeholder="Search your area..."
                                className="h-12 rounded-2xl bg-white/80 border-none"
                            />
                        </div>

                        {/* POPIA Consent Section */}
                        <div className="bg-gray-50/80 p-4 rounded-2xl space-y-3 border border-gray-100">
                            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Privacy & POPIA</h3>
                            <div className="space-y-2">
                                <p className="text-[11px] text-muted-foreground leading-snug">
                                    • Using aliases (nicknames) instead of addresses.<br />
                                    • Contact info only shared with chosen providers.<br />
                                    • Anonymized brand stats (never linked to you).
                                </p>
                            </div>
                            <label className="flex items-start gap-3 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    checked={popiaAccepted}
                                    onChange={(e) => setPopiaAccepted(e.target.checked)}
                                    className="mt-1 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary/20"
                                    required
                                />
                                <span className="text-xs text-slate-600 group-hover:text-slate-900 transition-colors">
                                    I have read and agree to the <Link to="/terms" target="_blank" className="text-primary font-semibold hover:underline">Terms of Service</Link> and POPIA data handling policy.
                                </span>
                            </label>
                        </div>

                        <Button
                            type="submit"
                            className="w-full mt-4 rounded-2xl py-7 text-lg font-bold shadow-lg shadow-primary/20 transition-all hover:shadow-primary/30 active:scale-[0.98]"
                            disabled={loading || !formData.fullName || !formData.propertyName || !formData.suburb || !popiaAccepted}
                        >
                            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Start Managing Your Home"}
                        </Button>
                    </form>
                </GlassCard>
            </div>
        </div>
    );
};

export default SetupAccount;
